import { Component, OnInit } from '@angular/core';
import {
  FormsClient,
  FormsFlag,
  ScheduleAEmailServiceInputTransactionType,
  SearchSortDirection,
  SearchSortField
} from '@benefitsui/app/api/benefits-clients';
import { createErrorControlMap, ErrorControl, TableData } from '@dxo/ng-swift-ui';
import { FormsService } from './forms.service';
import { FormCellItemType, LeterFormCellItemType } from './forms-page.types';
import { HttpErrorService } from '@benefitsui/app/services/http-error/http-error.service';
import { PolicyService } from '@benefitsui/app/services/policy/policy.service';
import {
  AbstractControl,
  FormBuilder,
  FormControl,
  FormGroup,
  UntypedFormArray,
  UntypedFormControl,
  UntypedFormGroup,
  Validators
} from '@angular/forms';
import { dateFromValidator, dateToValidator, dateValidator } from '@benefitsui/app/shared/validators/dateValidation';
import { ScheduleALetterSearchParameters } from '../scheduleA-page/scheduleA-page.types';
import { Subscription } from 'rxjs';
import { Defaults } from '@benefitsui/app/shared/defaults/defaults';
import { Constants } from '../billing-page/billing-page.types';

@Component({
  selector: 'app-taxforms-page',
  templateUrl: './tax-page.component.html',
  styleUrls: ['./tax-page.component.scss'],
  standalone: false
})
export class TaxFormsPageComponent implements OnInit {
  constructor(
    private formsClient: FormsClient,
    private formsService: FormsService,
    private errorService: HttpErrorService,
    private policyService: PolicyService,
    private fb: FormBuilder
  ) {
    this.formGroup = this.fb.group({
      fromDate: new FormControl<string>(Constants.StringEmpty, {
        validators: [Validators.required, dateValidator, dateFromValidator],
        updateOn: Defaults.blurString
      }),
      toDate: new FormControl<string>(Constants.StringEmpty, {
        validators: [Validators.required, dateValidator, dateToValidator],
        updateOn: Defaults.blurString
      })
    });

    this.formErrors = {
      ...this.getErrorMap(this.formGroup)
    };
  }

  formErrors: { [key: string]: ErrorControl };
  formGroup: FormGroup;
  isTaxLoading = true;
  isLetterLoading = true;
  taxDataTable!: TableData;
  scheduleADataTable!: TableData;
  scheduleAFormsCardData: LeterFormCellItemType[] = [];
  startDateLabel = `Start date (${Defaults.dateFormatStringUI})`;
  endDateLabel = `End date (${Defaults.dateFormatStringUI})`;
  taxBodyHeader = 'W9 forms';
  scheduleAHeader = 'Schedule A letters';
  requestHeader = 'Request custom Schedule A letters';
  hasTax = false;
  hasScheduleA = false;
  hasServerError = false;
  messageHeader = 'Schedule A letters are currently unavailable';
  messageBody = `We apologize for the system not operating as expected
and are working to fix it.`;
  seeMoreLessText = 'See more';
  searchParameter: ScheduleALetterSearchParameters = {
    page: 1,
    pageSize: 5,
    sortField: SearchSortField.StatementDate,
    sortDirection: SearchSortDirection.Descending
  };
  totalLines = 0;
  subscriptions: Subscription[] = [];
  // eslint-disable-next-line
  dateCharRegex = /[0-9\-\/]/;
  ngOnInit(): void {
    this.fillTaxData();
    this.fillLetterData();
    this.formGroup.controls.fromDate.valueChanges.subscribe((v) =>
      this.formGroup.controls.fromDate.setValue(this.formatDateWhenTyping(v), { emitEvent: false })
    );
    this.formGroup.controls.toDate.valueChanges.subscribe((v) =>
      this.formGroup.controls.toDate.setValue(this.formatDateWhenTyping(v), { emitEvent: false })
    );
  }

  fillTaxData(): void {
    this.formsClient.allForms().subscribe((response) => {
      this.formsService.setFormsData(response.data?.taxForms as FormCellItemType[], FormsFlag.TaxForm);
      const taxFormsData = this.formsService.getFormsData(FormsFlag.TaxForm);
      if (taxFormsData) {
        this.hasTax = taxFormsData.length > 0;
        this.taxDataTable = this.formsService.transformData(taxFormsData);
      }
      this.isTaxLoading = false;
    });
  }

  fillLetterData(): void {
    this.subscriptions.push(
      this.formsClient
        .scheduleALetter({
          sortDirection: this.searchParameter.sortDirection,
          page: this.searchParameter.page,
          pageSize: this.searchParameter.pageSize,
          sortField: this.searchParameter.sortField,
          policyId: undefined,
          policyIds: undefined
        })
        .subscribe(
          (x) => {
            this.totalLines = x.data?.totalPages ?? 0;
            if (this.totalLines === 0) {
              this.isLetterLoading = false;
            }
            this.formsService.setLetterFormsData(x.data?.letters as LeterFormCellItemType[]);
            const scheduleAFormsData = this.formsService.getLetterFormsData();
            this.scheduleAFormsCardData = scheduleAFormsData ?? [];
            if (scheduleAFormsData) {
              this.hasScheduleA = scheduleAFormsData.length > 0;
              this.scheduleADataTable = this.formsService.transformLetterData(scheduleAFormsData);
            }
            this.isLetterLoading = false;
          },
          () => {
            this.errorService.preventNextErrorRedirect();
            this.hasServerError = true;
            this.isLetterLoading = false;
          },
          () => {
            this.isLetterLoading = false;
          }
        )
    );
  }

  seeMoreLess(): void {
    if (this.searchParameter.pageSize === 5) {
      this.searchParameter.pageSize = 500;
      this.seeMoreLessText = 'See less';
    } else {
      this.searchParameter.pageSize = 5;
      this.seeMoreLessText = 'See more';
    }
    this.fillLetterData();
  }

  getErrorMap = (formGroup: FormGroup) =>
    createErrorControlMap(formGroup, {
      fromDate: {
        required: 'This is a required field.',
        dateFormat: `The date must be in a ${Defaults.dateFormatStringUI} format.`,
        checkYears: 'From or To Date is older than seven years.',
        checkDay: 'The date must be in the past.'
      },
      toDate: {
        required: 'This is a required field.',
        dateFormat: `The date must be in a ${Defaults.dateFormatStringUI} format.`,
        dateCompare: 'The from date must occur before the to date.',
        checkYears: 'From or To Date is older than seven years.',
        checkDay: 'The date must be in the past.'
      }
    }) as object as {
      fromDate: ErrorControl;
      toDate: ErrorControl;
    };

  updateControls(control: AbstractControl) {
    if (control instanceof UntypedFormControl) {
      control.updateValueAndValidity();
    } else if (control instanceof UntypedFormGroup) {
      Object.values(control.controls).forEach((childControl) => this.updateControls(childControl));
    } else if (control instanceof UntypedFormArray) {
      control.updateValueAndValidity();
      control.controls.forEach((childControl) => this.updateControls(childControl));
    }
  }

  open = false;
  modalSuccess = false;
  async submit() {
    this.request();
  }

  pageValid() {
    const dateFrom = this.formGroup.controls.fromDate.value;
    const dateTo = this.formGroup.controls.toDate.value;

    const dateFromNumber = this.dateParse(dateFrom);
    const dateFromValid = this.dateValid(dateFrom);

    const dateToNumber = this.dateParse(dateTo);
    const dateToValid = this.dateValid(dateTo);

    const pageValid =
      dateFromValid && dateToValid && !isNaN(dateFromNumber) && !isNaN(dateToNumber) && dateFromNumber <= dateToNumber;

    return pageValid;
  }

  request() {
    if (this.formGroup.invalid) {
      this.updateControls(this.formGroup);
      return;
    }
    this.open = true;
    this.formsClient
      .requestScheduleALetter({
        dateFrom: this.formGroup.controls.fromDate.value!,
        dateTo: this.formGroup.controls.toDate.value!,
        policyId: this.policyService.policyNumber,
        policyName: this.policyService.policyName,
        requestorUserName: undefined,
        requestorName: undefined,
        requestorEmail: undefined,
        transactionType: ScheduleAEmailServiceInputTransactionType.ScheduleA
      })
      .subscribe((response) => {
        if (response.messages && response.messages?.length > 0) {
          console.log('Something wrong');
        } else {
          this.formGroup.markAsPristine();
          this.modalSuccess = true;
        }
      });
  }
  closePopup() {
    this.open = false;
  }

  generateFileName(item: string): string {
    return 'ScheduleALetter-' + item;
  }

  private dateParse(dateValue: string): number {
    const dateNumber = Date.parse(dateValue).valueOf();
    return dateNumber;
  }

  private dateValid(dateValue: string): boolean {
    const dateValidForm = Defaults.dateRegex.test(dateValue);

    const dateNumber = this.dateParse(dateValue);
    const dateNow = Date.now();

    const valid = dateValidForm && !isNaN(dateNumber) && dateNumber <= dateNow;

    return valid;
  }

  private formatDateWhenTyping(value: string | null) {
    if (value === null) {
      return '';
    }
    value = value.split('-').join('/');
    const parts = value.split('/');
    for (let i = 0; i < parts.length - 1; i++) {
      parts[i] = parts[i].padStart(2, '0');
    }

    value = parts.join('/');
    if (value.length > 2 && !isNaN(parseInt(value[2], 10))) {
      value = value.slice(0, 2) + '/' + value[2];
    }
    if (value.length > 5 && !isNaN(parseInt(value[5], 10))) {
      value = value.slice(0, 5) + '/' + value[5];
    }
    return value;
  }
}
